import React from 'react';
import { useTranslate } from 'react-admin';
import {
  Alert, Box, Card, Grid, Skeleton, Stack, TableBody, TableCell, TableHead, TableRow, Typography,
} from '@mui/material';
import {
  BlockHeading, DataTable, KpiTile, PageTitle, SectionHeader, StatusDot,
} from '@/components/shell';
import { healthTone, useStatFormat } from '@/features/statistics/accountHealth';
import { useSystemInfo } from '../hooks/useSystemInfo';
import { GatewayHealthCard } from './GatewayHealthCard';
import { HealthDot } from './HealthDot';
import { LatencyBar } from './LatencyBar';
import type { ServiceHealth } from '../types';

/** What a health check probed, in words. */
const ServiceName: React.FC<{ check: ServiceHealth }> = ({ check }) => {
  const t = useTranslate();
  switch (check.kind) {
    case 'gateway':
      return <>{t('tech_support.kpi.gateway')}</>;
    case 'ocr-primary':
      return <>{t('screens.techSupport.ocrCheck', { role: t('screens.techSupport.primary'), name: check.name })}</>;
    case 'ocr-secondary':
      return <>{t('screens.techSupport.ocrCheck', { role: t('screens.techSupport.secondary'), name: check.name })}</>;
    default:
      return <>{check.name}</>;
  }
};

export const SystemOverview: React.FC = () => {
  const t = useTranslate();
  const fmt = useStatFormat();
  const { systemInfo, loading, error, refresh } = useSystemInfo();

  const summary = systemInfo?.summary;
  const errorRate = summary?.weeklyErrorRate ?? null;
  const gatewayCheck = systemInfo?.healthChecks.find((hc) => hc.kind === 'gateway');
  const gatewayUp = gatewayCheck?.status === 'healthy';
  const online = summary?.onlineServers ?? null;
  const total = summary?.totalServers ?? null;
  const servicesKnown = online !== null && total !== null;
  const checks = systemInfo?.healthChecks ?? [];

  const infoItems = [
    { label: t('tech_support.system_info.version'), value: systemInfo?.application.version },
    { label: t('tech_support.system_info.environment'), value: systemInfo?.application.environment },
    { label: t('tech_support.system_info.gatewayUrl'), value: systemInfo?.application.gatewayUrl },
  ];

  return (
    <Box>
      <SectionHeader
        left={<PageTitle title={t('tech_support.tabs.overview')} subtitle={t('screens.techSupport.overviewSubtitle')} />}
        onRefresh={refresh}
        refreshing={loading}
      />

      {error && !systemInfo && (
        <Alert severity="error" sx={{ mb: 2 }}>{t('screens.techSupport.loadFailed')}</Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}>
          <KpiTile
            label={t('tech_support.kpi.gateway')}
            textValue
            value={gatewayCheck ? t(gatewayUp ? 'screens.techSupport.online' : 'screens.techSupport.offline') : null}
            detail={gatewayCheck?.latencyMs != null ? `${gatewayCheck.latencyMs} ms` : undefined}
            loading={loading}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <KpiTile
            label={t('tech_support.kpi.kycServices')}
            value={servicesKnown ? `${online}/${total}` : null}
            detail={servicesKnown
              ? <StatusDot tone={total === 0 ? 'attention' : online === total ? 'ok' : 'problem'} label={t('tech_support.kpi.serversOnline')} />
              : undefined}
            loading={loading}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <KpiTile
            label={t('tech_support.kpi.accounts')}
            value={summary?.totalAccounts != null ? fmt.count(summary.totalAccounts) : null}
            detail={t('screens.techSupport.accountsDetail')}
            loading={loading}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <KpiTile
            label={t('tech_support.kpi.errorRate')}
            value={errorRate !== null ? fmt.rate(errorRate) : null}
            detail={errorRate !== null
              ? <StatusDot tone={healthTone(errorRate)} label={t('screens.techSupport.lastSevenDays')} />
              : summary?.weeklyRequests === 0 ? t('screens.techSupport.noRequestsWeek') : undefined}
            loading={loading}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={5}>
          <Box component="section" aria-labelledby="system-info" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <BlockHeading id="system-info" title={t('tech_support.system_info.title')} />
            <Card sx={{ p: 2, flex: 1 }}>
              <Stack component="dl" spacing={1.5} sx={{ m: 0 }}>
                {infoItems.map((item) => (
                  <Box key={item.label}>
                    <Typography component="dt" variant="body2" color="text.secondary">{item.label}</Typography>
                    <Typography component="dd" variant="body2" sx={{ m: 0, overflowWrap: 'anywhere' }}>
                      {loading ? <Skeleton width="50%" /> : item.value || '—'}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Card>
          </Box>
        </Grid>
        <Grid item xs={12} md={7}>
          <GatewayHealthCard health={systemInfo?.gatewayHealth ?? null} loading={loading} />
        </Grid>
      </Grid>

      <Box component="section" aria-labelledby="health-checks">
        <BlockHeading id="health-checks" title={t('tech_support.health_checks.title')} />
        {loading ? (
          <Card sx={{ p: 2 }}>{[...Array(3)].map((_, i) => <Skeleton key={i} height={40} />)}</Card>
        ) : (
          <DataTable label={t('tech_support.health_checks.title')}>
            <TableHead>
              <TableRow>
                <TableCell>{t('tech_support.health_checks.service')}</TableCell>
                <TableCell>{t('tech_support.health_checks.status')}</TableCell>
                <TableCell sx={{ minWidth: 180 }}>{t('tech_support.health_checks.latency')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {checks.map((hc) => (
                <TableRow key={`${hc.kind}-${hc.name}`}>
                  <TableCell><ServiceName check={hc} /></TableCell>
                  <TableCell><HealthDot status={hc.status} tooltip={hc.error} /></TableCell>
                  <TableCell><LatencyBar latencyMs={hc.latencyMs} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </DataTable>
        )}
      </Box>
    </Box>
  );
};
